import { isEmpty } from 'lodash';
import moment from 'moment';
import { put, select, takeLatest } from 'redux-saga/effects';
import { RootState } from '../..';
import { checkEmptyObj, getLookUpFullDetail, prepareAttachment, prepareClassifications, prepareExtension } from '../../../modules/crm/inPatient/service/crmUtils';
import { COMMON_ERROR } from '../../../shared/constants/commonConstants';
import { GLOBAL_URL } from '../../../shared/constants/dataServiceUrl';
import ApplicationDetail from '../../../shared/services/applicationDetail.service';
import SendRequest from '../../../shared/services/sendRequest.service';
import { setDOA, setHidConfirmationMessage, setHospActionId, setIsMinimize } from '../../actions/newAdmissionAction';
import { IClaimActionAdditionalParams, NewAdmissionState } from '../../models/newAdmissionModel';
import actionTypes from '../../types/reimbursementTypes';

export function* submitAdmission({ form, isEdit, onSuccess, onError }: any): Generator {
  try {
    const appDetail = ApplicationDetail.instance;

    // ! GETTING DATA
    const {
      classificationArray, uploadedFiles, defaultTimeline: { remarks }, isManualSubmission: memberSearchFailed,
      additionalAdmissionInfo, billAmountArray, isDraftSelected,
    }: any = yield select((state: RootState): NewAdmissionState => state.newAdmission);

    const {
      payerList: { payer: { id: payerId, uniqueIdentifier, productCode, isOnMailPayer, isCorporatePayer } },
      payerLookUp,
      newAdmission,
      admissionDetails,
      mailBox: { hospitalConfiguration } = {} as any,
    }: any = yield select((state: RootState): RootState => state);

    const {
      documentType: documentTypeLookUp = [],
    } = payerLookUp[uniqueIdentifier] || {};

    const {
      memberAge: patientAge, memberGenderSex: genderSex, memberGenderSexRadio: genderRadio, dateOfBirth: dob,
      isRulesConfigured, policyEndDate, policyStartDate, courierTrackId, courierProvider, switchClick,
    } =
      newAdmission?.selectedBeneficiaryDetail ||
      (
        isEmpty(admissionDetails?.claimDetails)
          ? newAdmission?.admissionFormVal?.customerDetail
          : admissionDetails?.claimDetails
      );
    const { userInfo }: any = yield select((state: RootState): any => state?.idCard);
    const { phrAddress, gender, yearOfBirth, monthOfBirth, dayOfBirth } = userInfo;
    const dateOfBirth = dob || ((yearOfBirth && monthOfBirth && dayOfBirth) ? `${yearOfBirth}-${monthOfBirth}-${dayOfBirth}` : undefined);
    const attachments = uploadedFiles.filter(({ existingRecord }: any): boolean => !existingRecord);
    // ! FETCHING FORM DATA

    const {
      fullName: patientName, mobile, emailId: email, policyNumber: policyNo, memberId,
      corporateName: policyHolderName, dateOfAdmission, dateOfDischarge, procedure, beneficiaryAccountNumber,
      claimedAmount, token } = form.getFieldsValue();

    let action: any = 'REIMBURSEMENTCLAIMSUBMITTED';
    let payerClaimNo = null;
    let hospActionId = null;

    if (isEdit) {
      const { nextAllowStatus, payerClaimNo: claimNumber, hospActionId: hospitalActionId }: any =
        yield select((state: RootState): any => state.admissionDetails.claimDetails);
      if (nextAllowStatus?.length) {
        [action] = nextAllowStatus;
        payerClaimNo = claimNumber;
        hospActionId = hospitalActionId;
      } else {
        action = null;
      }
    } else if (isDraftSelected) {
      const { claimDetails } = admissionDetails;
      action = claimDetails?.nextAllowStatus?.[0] || 'NEWADMISSION';
      hospActionId = claimDetails?.hospActionId;
      payerClaimNo = claimDetails?.payerClaimNo;
    }

    // ! FORM VALIDATION
    if (hospitalConfiguration?.passwordExpire && (isOnMailPayer || isCorporatePayer)) {
      return onError('PASSWORD_EXPIRED');
    }
    if (dateOfAdmission && dateOfDischarge) {
      if (moment(dateOfAdmission).isAfter(moment(dateOfDischarge))) {
        return onError('Please select the admission date less than the discharge date.');
      }
      if ((policyStartDate && policyEndDate) && (dateOfAdmission.isAfter(moment(policyEndDate).add(2, 'day')) ||
        moment(policyStartDate).isAfter(moment(dateOfAdmission)))) {
        return onError('Please select admission date after policy start date and before policy end date.');
      }
    }

    if (!attachments?.length && action !== 'EDIT') {
      yield put(setIsMinimize(false));
      return onError('NA');
    }

    let finalRemark;
    if (!checkEmptyObj(remarks)) {
      finalRemark = Object.values(remarks || {}).filter((msg: any): boolean => msg).join(', ');
    }

    const payerExtension = prepareExtension(
      {
        isRulesConfigured,
        policyStartDate,
        policyEndDate,
        memberSearchFailed,
        remarks,
        additionalAdmissionInfo,
        courierTrackId,
        switchClick,
        courierProvider,
        dateOfBirth,
        action,
        beneficiaryAccountNumber,
      },
    );
    // ! FINAL DTO
    let newclaimedAmount;
    if (claimedAmount) {
      newclaimedAmount = claimedAmount * 1;
    }
    const requestPayload = {
      action,
      hospActionId,
      payerClaimNo,
      patientAge: isNaN(parseInt(patientAge, 10)) ? undefined : parseInt(patientAge, 10),
      patientGender: (genderSex || genderRadio),
      patientName,
      policyHolderName,
      policyNo,
      doa: moment(dateOfAdmission)?.format(),
      dod: moment(dateOfDischarge)?.format(),
      memberId,
      mobileNo: (mobile)?.substr(4)?.split(' ')?.join(''),
      email, payerId, claimedAmount: newclaimedAmount,
      payerClaimExtensionFields: payerExtension,
      attachments: prepareAttachment(attachments, documentTypeLookUp),
      providerId: appDetail?.getProviderId(),
      classifications: prepareClassifications(classificationArray, attachments),
      schemeType: productCode,
      remarks: finalRemark,
      phraddress: phrAddress ? phrAddress[0] : undefined,
      gender,
      token,
      billAmountArray,
      procedureDesc: getLookUpFullDetail([procedure], payerLookUp[uniqueIdentifier].diagnosis, true),
    };
    yield put(setDOA(requestPayload?.doa));
    yield claimActionService(requestPayload, onSuccess, onError, {isOnMailPayer, isCorporatePayer});
  } catch (e) {
    console.error(e);
    // onError(COMMON_ERROR);
  }
}

function* claimActionService(requestPayload: any, onSuccess: () => {}, onError: any, healthId: any = null,
  additionalParams?: IClaimActionAdditionalParams): Generator {
  const sendRequest = SendRequest.instance;
  try {
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Claim/RIClaimAction`;
    const newAdmissionResponse: any = yield sendRequest.post(url, requestPayload, {
      isShowErrorInInterceptor: false });
    const { isSuccess = false, errors = [], hipOnConfirmResponseDTO = null, hospActionId } = newAdmissionResponse || {};
    if (isSuccess) {
      yield put (setHospActionId(hospActionId));
      const confirmationMessage = healthId ?
        hipOnConfirmResponseDTO !== null ? 'ABHA health record is linked.' : 'Demographic Data Mismatch. Unable to link patient record.' : 'ABHA health record is not linked because health id is not provided.';
      yield put(setHidConfirmationMessage(confirmationMessage));
      onSuccess();
    } else {
      onError(errors.join(', '));
    }
  } catch ({ response: { data } }: any) {
    const { errors, errorMessage }: any = data || {};
    if (errors instanceof Array) {
      onError(errors?.join(', ') || errorMessage);
    } else {
      onError(COMMON_ERROR);
    }
  }
}

export function* generateOTP({ payload, onSuccess, onError }: any): Generator {
  yield generateOTPService(payload, onSuccess, onError);
}

export function* generateOTPService( payload: any, onSuccess: any, onError: any ): Generator {
  const sendRequest = SendRequest.instance;
  try {
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/GenerateVerifyOTP`;
    const generateOTPResponse: any = yield sendRequest.post(url, payload, {
      isShowErrorInInterceptor: false });
    const { isSuccess = false, errors = [], encryptedToken = ''} = generateOTPResponse || {};
    if (isSuccess) {
      onSuccess(encryptedToken);
    } else {
      onError(errors.join(', '));
    }
  } catch (response: any) {
    const { errors, errorMessage }: any = response || {};
    if (errors instanceof Array) {
      onError(errors?.join(', ') || errorMessage);
    } else {
      onError(COMMON_ERROR);
    }
  }

}
export function* verifyOTP({ payload, onSuccess, onError}: any): Generator {
  yield verifyOTPService(payload, onSuccess, onError);
}

export function* verifyOTPService(payload: any, onSuccess: any, onError: any): Generator {
  const sendRequest = SendRequest.instance;
  try {
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/GenerateVerifyOTP`;
    const verifyOTPResponse: any = yield sendRequest.post(url, payload, {
      isShowErrorInInterceptor: false });
    const { isSuccess = false, errorMessage, isVerified = false} = verifyOTPResponse || {};
    if (isSuccess) {
      if (isVerified) {
        onSuccess();
      } else {
        onError(errorMessage);
      }
    } else {
      onError(errorMessage);
    }
  } catch (response: any) {
    const { errors, errorMessage }: any = response || {};
    if (errors instanceof Array) {
      onError(errors?.join(', ') || errorMessage);
    } else {
      onError(COMMON_ERROR);
    }
  }
}

export function* submitIr({ payload: { form, onSuccess, onError } }: any): Generator {
  try {
    const {
      reimbursement: { reimbursementDetail : {
        payerClaimNo = '', hospActionId = '', nextAllowStatus = [], payerActualId: payerId = '',
      } = {} } = {},
      newAdmission: { uploadedFiles = [], defaultTimeline = {}, additionalAdmissionInfo = {} } = {},
      payerList: { payer = {} },
      mailBox: { hospitalConfiguration } = {} as any,
    }: any = yield select((state: RootState): any => state);
    const { additionalEmails} = form?.getFieldsValue(['additionalEmails']) || {};
    const attachments = uploadedFiles.filter(({ existingRecord }: any): boolean => !existingRecord);
    const { isOnMailPayer, isCorporatePayer } = payer;
    const { remarks } = defaultTimeline || {};
    if (hospitalConfiguration?.passwordExpire && (isOnMailPayer || isCorporatePayer)) {
      return onError('PASSWORD_EXPIRED');
    }
    let finalRemark;
    if (!checkEmptyObj(remarks)) {
      finalRemark = Object.values(remarks || {}).filter((msg: any): boolean => msg).join(', ');
    }

    const payerExtension = prepareExtension({ remarks, additionalEmails, additionalAdmissionInfo, action: nextAllowStatus[0] });

    const requestPayload = {
      action: nextAllowStatus[0], hospActionId, payerClaimNo, payerId,
      attachments: prepareAttachment(attachments),
      classifications: [],
      payerClaimExtensionFields: payerExtension,
      remarks: finalRemark,
    };
    yield claimActionService(requestPayload, onSuccess, onError, null, { isOnMailPayer, isCorporatePayer });
  } catch (e) {
    onError(COMMON_ERROR);
  }
}

export function* watchReimbursementNewAdmission(): any {
  yield takeLatest(actionTypes.SUBMIT_REIMBURSEMENT_ADMISSION, submitAdmission);
  yield takeLatest(actionTypes.GENERATE_REIMBURSEMENT_OTP, generateOTP);
  yield takeLatest(actionTypes.VERIFY_REIMBURSEMENT_OTP, verifyOTP);
  yield takeLatest(actionTypes.SUBMIT_REIMBURSEMENT_IR, submitIr);
}

export default watchReimbursementNewAdmission;
